/**
 * Static product data for SEO/crawler visibility
 * This data renders immediately on first paint before async fetch completes
 * Updated periodically to reflect current bestsellers
 */

export interface StaticProduct {
  id: string;
  name: string;
  category: string;
  subcategory: string | null;
  price: number;
  description: string | null;
  concern: string | null;
  image_url: string | null;
  in_stock: boolean;
}

// Curated bestseller products (matches FeaturedProducts component)
export const staticBestsellers: StaticProduct[] = [
  {
    id: "95ae67fe-90d9-4926-aa9d-635ffc3da041",
    name: "Acwell Licorice Ph Balancing Cleansing Toner 150ml",
    category: "Toner",
    subcategory: null,
    price: 1600000,
    description: "Elevate your skincare routine with Acwell's Licorice pH Balancing Cleansing Toner. This gentle yet effective toner is formulated to balance your skin's pH level while providing deep cleansing.",
    concern: "Hydration",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/acwell-toner.jpg",
    in_stock: true,
  },
  {
    id: "728fbdad-3ff1-4861-bd34-582c5c3fc129",
    name: "Tiam Vita B3 Source 40ml",
    category: "Serum",
    subcategory: null,
    price: 1800000,
    description: "A powerful niacinamide serum that helps brighten skin tone, minimize pores, and improve overall skin texture. Perfect for achieving a radiant, healthy-looking complexion.",
    concern: "Brightening",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/tiam-vita-b3.jpg",
    in_stock: true,
  },
  {
    id: "5a04e42d-42a3-4309-a83f-eabbd1e037e1",
    name: "Kojie San Skin Lightening Soap",
    category: "Cleanser",
    subcategory: null,
    price: 350000,
    description: "A popular kojic acid soap known for its skin brightening properties. Helps reduce the appearance of dark spots and hyperpigmentation for a more even skin tone.",
    concern: "Brightening",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/kojie-san.jpg",
    in_stock: true,
  },
  {
    id: "8c27e38c-6e31-479e-b305-06bd2905b08e",
    name: "Cosrx Advanced Snail 96 Mucin Power Essence",
    category: "Serum",
    subcategory: null,
    price: 2200000,
    description: "A lightweight essence containing 96% snail secretion filtrate to hydrate, repair, and nourish the skin. Great for achieving smooth, healthy-looking skin.",
    concern: "Hydration",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/cosrx-snail-essence.jpg",
    in_stock: true,
  },
  {
    id: "4864ccf8-658b-4273-944a-5390a2659513",
    name: "Cosrx Advanced Snail 92 All In One Cream",
    category: "Moisturizer",
    subcategory: null,
    price: 2400000,
    description: "An all-in-one cream with 92% snail secretion filtrate that deeply moisturizes and repairs damaged skin. Perfect for all skin types seeking intense hydration.",
    concern: "Hydration",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/cosrx-snail-cream.jpg",
    in_stock: true,
  },
  {
    id: "52fca10d-b357-4106-b786-392e38e4a44b",
    name: "Skin Aqua UV Super Moisture Gel SPF 50+ PA+++ 110g",
    category: "Sunscreen",
    subcategory: null,
    price: 1500000,
    description: "A lightweight, hydrating sunscreen gel with high SPF protection. Water-resistant formula perfect for daily use in tropical climates like Nigeria.",
    concern: "Sun Protection",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/skin-aqua-spf50.jpg",
    in_stock: true,
  },
  {
    id: "175771ac-d332-40bb-b6c2-6203ad444f7d",
    name: "Anua Niacinamide 10% + TXA 4% Serum 30ml",
    category: "Serum",
    subcategory: null,
    price: 2000000,
    description: "A potent serum combining niacinamide and tranexamic acid to target hyperpigmentation, uneven skin tone, and enlarged pores for a clearer complexion.",
    concern: "Brightening",
    image_url: "https://nkidurjikumsxkebpvuw.supabase.co/storage/v1/object/public/product-images/products/anua-niacinamide.jpg",
    in_stock: true,
  },
];

// Extended product list for Products page initial render
// Includes bestsellers plus additional popular products
export const staticAllProducts: StaticProduct[] = [
  ...staticBestsellers,
  // Add more products to ensure good initial page content
  {
    id: "cleanser-cerave-01",
    name: "CeraVe Hydrating Facial Cleanser",
    category: "Cleanser",
    subcategory: null,
    price: 1200000,
    description: "A gentle, non-foaming cleanser that removes dirt and makeup while maintaining the skin's natural moisture barrier. Contains ceramides and hyaluronic acid.",
    concern: "Hydration",
    image_url: null,
    in_stock: true,
  },
  {
    id: "moisturizer-cerave-01",
    name: "CeraVe Moisturizing Cream",
    category: "Moisturizer",
    subcategory: null,
    price: 1800000,
    description: "A rich, non-greasy moisturizing cream with essential ceramides and hyaluronic acid to restore and maintain the skin's natural barrier.",
    concern: "Hydration",
    image_url: null,
    in_stock: true,
  },
  {
    id: "sunscreen-la-roche-01",
    name: "La Roche-Posay Anthelios UV Mune 400",
    category: "Sunscreen",
    subcategory: null,
    price: 2500000,
    description: "Advanced sun protection with broad spectrum UVA/UVB filters. Lightweight, non-greasy formula suitable for sensitive skin.",
    concern: "Sun Protection",
    image_url: null,
    in_stock: true,
  },
  {
    id: "serum-ordinary-01",
    name: "The Ordinary Niacinamide 10% + Zinc 1%",
    category: "Serum",
    subcategory: null,
    price: 800000,
    description: "A high-strength vitamin and mineral formula that reduces the appearance of blemishes, pores, and excess oil production.",
    concern: "Pores",
    image_url: null,
    in_stock: true,
  },
  {
    id: "toner-some-by-mi-01",
    name: "Some By Mi AHA BHA PHA 30 Days Miracle Toner",
    category: "Toner",
    subcategory: null,
    price: 1400000,
    description: "A gentle exfoliating toner with AHA, BHA, and PHA to help clear pores, smooth skin texture, and improve overall skin clarity.",
    concern: "Acne",
    image_url: null,
    in_stock: true,
  },
];
